import { inject } from "@angular/core";
import { UrlTree, Router, ActivatedRouteSnapshot } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { ConfigService } from "../../shared/config/config.service";
import { mergeDocumentLanguages } from "../../shared/helpers/document-languages.helper";
import { PiaDocumentsService } from "../../shared/pia-api/pia-documents.service";
import { PiaProductsService } from "../../shared/pia-api/pia-products.service";
import { DocumentLanguagesStorageService } from "../../shared/services/document-languages-storage.service";

/**
 * Async redirect to the first available document category for a product.
 */
export async function productDetailRedirect(
  redirectData: Pick<ActivatedRouteSnapshot, "url" | "params">,
): Promise<UrlTree> {
  const piaProductsService = inject(PiaProductsService);
  const piaDocumentsService = inject(PiaDocumentsService);
  const documentLanguagesStorageService = inject(
    DocumentLanguagesStorageService,
  );
  const config = inject(ConfigService);
  const router = inject(Router);

  // Extract params from url or params
  // url: [ 'pia', country, language, year, 'product', id ]
  const url = redirectData.url;
  const params = redirectData.params;
  const countryID = params["country"] || url[1]?.path;
  const language = params["language"] || url[2]?.path;
  const year = Number(params["year"] || url[3]?.path);
  const productID = params["id"] || url[5]?.path;

  // Get document languages
  const primaryDocumentLanguages = await firstValueFrom(
    documentLanguagesStorageService.primaryDocumentLanguages$,
  );
  const secondaryDocumentLanguages = await firstValueFrom(
    documentLanguagesStorageService.secondaryDocumentLanguages$,
  );
  const availableLanguages = await firstValueFrom(
    piaDocumentsService.getAvailableLanguages(),
  );
  const productIDs = await firstValueFrom(
    piaProductsService.getProductDisplayRangesProductIDs({
      countryID: countryID.toUpperCase(),
      languageID: language,
      year,
      productID,
    }),
  );

  const includedLanguages = mergeDocumentLanguages(
    primaryDocumentLanguages,
    secondaryDocumentLanguages,
    countryID,
    language.toLowerCase(),
  );

  const defaultLanguage = config.languageDefault;
  const selectedLanguage =
    (availableLanguages?.includes(language) ? language : null) ||
    defaultLanguage;

  // Get categories for product
  const categories = await firstValueFrom(
    piaDocumentsService.getUniqueDocCatsByLanguage({
      languageID: selectedLanguage,
      countryID,
      year,
      productIDs,
      includedLanguages,
    }),
  );

  if (Array.isArray(categories) && categories.length > 0 && categories[0]?.ID) {
    return router.createUrlTree([
      "pia",
      countryID,
      selectedLanguage,
      year,
      "product",
      productID,
      "documents",
      categories[0].ID,
    ]);
  }

  return router.createUrlTree([
    "pia",
    countryID,
    selectedLanguage,
    year,
    "product",
    productID,
    "documents",
    "not-found",
  ]);
}
